/*
Shared helpers for the example-scenario catalog (backed by /list_examples).

Centralizes:
  - model-type presentation metadata (verbose label, mini-name),
  - flattening the `summary` stat array for card subtitles,
  - a hook that lazily loads the catalog once.

Keeping this in one module means the gallery and the dynamic /examples route
stay consistent and fully data-driven off the endpoint.
*/
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchExampleList } from "store/reducers/thunks/scenarioThunks";
import { selectExampleCatalogStatus } from "store/reducers/uiReducer";

const modelPages = require("store/data/model-pages.json");

// model_type -> presentation. Falls back gracefully for unknown types so a new
// backend model type never breaks the gallery.
const MODEL_TYPE_META = {
  "vehicle-routing": { verbose: "Vehicle Routing", miniName: "VR" },
  "pickup-delivery": { verbose: "Pickup & Dropoff", miniName: "PD" },
};

export function getModelTypeMeta(modelType) {
  const meta = MODEL_TYPE_META[modelType] || {};
  return {
    verbose: meta.verbose || modelType || "",
    miniName: meta.miniName || "EX",
    // Destination for the "build from scratch" flow.
    page: modelPages[modelType] || "/model-builder",
  };
}

/**
 * Flatten the `summary` array of single-key stat objects into a tidy list.
 * e.g. [{ "Locations": 17 }, { "Trips": 12 }] -> [{label:"Locations", value:17}, ...]
 * Best-effort: unknown shapes are skipped, never thrown.
 */
export function flattenSummary(summary) {
  if (!Array.isArray(summary)) return [];
  const out = [];
  summary.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    Object.entries(entry).forEach(([label, value]) => {
      out.push({ label, value });
    });
  });
  return out;
}

/**
 * Lazily fetch the example catalog once. Safe to call from multiple mounted
 * components — it only dispatches while the catalog is idle.
 */
export function useEnsureExampleCatalog() {
  const dispatch = useDispatch();
  const status = useSelector(selectExampleCatalogStatus);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchExampleList());
    }
  }, [dispatch, status]);
}
